import { type DocumentNode, useApolloClient } from '@apollo/client';
import {
  MINUTE,
  Modal,
  type ModalHook,
  useControlledModalState,
} from '@leland-dev/leland-ui-library';
import React, {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { type Scalars } from '../../../__generated-gql-types__/globalTypes';
import { LexSlug, useLex } from '../../../context/LexContext';
import mxpnl from '../../../utils/analytics/mixpanel';
import { SCHEDULE_MODAL_EVENTS } from '../../../utils/analytics/mixpanel-events';
import phog from '../../../utils/analytics/posthog/posthog';
import { getDateInfo } from '../../../utils/session';

import { AddAdditionalTime } from './ScheduleModal/AddAdditionalTime';
import { AssignExistingOrder } from './ScheduleModal/AssignExistingOrder';
import { CategorySelection } from './ScheduleModal/CategorySelection';
import { CoachCalendar } from './ScheduleModal/CoachCalendar';
import { PackageSelection } from './ScheduleModal/PackageSelection';
import { PaymentDetails } from './ScheduleModal/PaymentDetails';
import { RecurringSessionDetails } from './ScheduleModal/RecurringSessionDetails';
import { ScheduleMessageModal } from './ScheduleModal/ScheduleMessageModal';
import { SchedulingSuccessModal } from './ScheduleModal/SchedulingSuccessModal';
import {
  type ScheduleModalCoachFragment,
  type ScheduleModalCoachingSessionFragment,
  ScheduleModalGetSavedCardsDocument,
  type ScheduleModalOfferingPackageFragment,
  type ScheduleModalOrderFragment,
  type ScheduleModalRecurringCadenceFragment,
  useScheduleModalEnoughTimeToScheduleLazyQuery,
  useScheduleModalGetSavedCardsQuery,
  useScheduleModalGetUnassignedOrdersQuery,
  useScheduleModalMatchSkusByTimeQuery,
  useScheduleModalUserQuery,
} from './__generated-gql-types__/ScheduleModal.generated';
interface ScheduleModalHookProps {
  coach: ScheduleModalCoachFragment;
  categoryUrn: Nullable<Scalars['CategoryUrn']['output']>;
  coachingSession?: Possible<ScheduleModalCoachingSessionFragment>;
  defaultStartTime?: Possible<number>;
  onSchedule?: (time?: Possible<number>) => void;
  refetchQueries?: DocumentNode[];
  defaultSessionType?: SessionOption;
  sidebarLocked?: boolean;
  onSuccessModalDismiss?: () => void;
}
interface ScheduleModalProps extends ScheduleModalHookProps {
  open: boolean;
  setOpen: (open: boolean) => void;
}

export enum ScheduleModalStep {
  CALENDAR = 'CALENDAR',
  PKG_SELECTION = 'PKG_SELECTION',
  PAYMENT_DETAILS = 'PAYMENT_DETAILS',
  MESSAGE = 'MESSAGE',
  PURCHASE = 'PURCHASE',
  SUCCESS = 'SUCCESS',
  CATEGORY_SELECTION = 'CATEGORY_SELECTION',
  ASSIGN_ORDER = 'ASSIGN_ORDER',
  RECURRING_SESSION_SELECTION = 'RECURRING_SESSION_SELECTION',
}

export enum SessionOption {
  INTRO_CALL = 'INTRO_CALL',
  COACHING_SESSION = 'COACHING_SESSION',
}

const ScheduleModalContent: FC<ScheduleModalProps> = ({
  coach,
  open,
  setOpen: setOpenDirect,
  categoryUrn,
  coachingSession,
  onSchedule,
  refetchQueries,
  defaultStartTime,
  defaultSessionType,
  sidebarLocked,
  onSuccessModalDismiss,
}) => {
  const { isLexEnabled } = useLex();

  const [selectedTime, setSelectedTime] =
    useState<Possible<number>>(defaultStartTime);
  const [step, setStep] = useState<ScheduleModalStep>(
    ScheduleModalStep.CALENDAR,
  );
  const [scheduledSessionId, setScheduledSessionId] =
    useState<Scalars['CoachingSessionUrn']['output']>();

  const [orderToAssign, setOrderToAssign] =
    useState<Nullable<ScheduleModalOrderFragment>>(null);

  const [offeringPackages, setOfferingPackages] = useState<
    Map<string, ScheduleModalOfferingPackageFragment>
  >(new Map());
  const [isPayAsYouGo, setIsPayAsYouGo] = useState<boolean>(false);

  const [enoughSelfServeTimeToSchedule, setEnoughSelfServeTimeToSchedule] =
    useState<boolean>(false);

  const [justThisSession, setJustThisSession] = useState<boolean | null>(null);

  const [recurringCadence, setRecurringCadence] = useState<
    Possible<ScheduleModalRecurringCadenceFragment>
  >(coachingSession?.recurringSession?.cadence ?? null);

  const [enoughTimeToScheduleAction] =
    useScheduleModalEnoughTimeToScheduleLazyQuery();

  const { data: savedCardsData } = useScheduleModalGetSavedCardsQuery();

  const [sessionType, setSessionTypeDefault] = useState<SessionOption>(
    defaultSessionType ??
      (coachingSession &&
      coachingSession?.content.__typename === 'OneOnOneSession' &&
      coachingSession.content.introOrder?.introCall
        ? SessionOption.INTRO_CALL
        : coachingSession
          ? SessionOption.COACHING_SESSION
          : coach.coachingRelationship
            ? SessionOption.COACHING_SESSION
            : SessionOption.INTRO_CALL),
  );

  const { hasUnlimitedPackage = false, availableTime = 0 } =
    coach.coachingTimeSummary ?? {};

  const availableCoachingTimeInMinutes = useMemo(() => {
    if (hasUnlimitedPackage) return Infinity;

    return availableTime;
  }, [availableTime, hasUnlimitedPackage]);

  const [sessionLength, setSessionLength] = useState<number>(
    sessionType === SessionOption.COACHING_SESSION
      ? coachingSession
        ? coachingSession.durationInMinute
        : availableCoachingTimeInMinutes >= 60 ||
            availableCoachingTimeInMinutes < 15
          ? 60
          : Math.floor(availableCoachingTimeInMinutes / 15) * 15
      : 15,
  );

  const [categoryToUse, setCategoryToUse] = useState<
    Nullable<Scalars['CategoryUrn']['output']>
  >(
    categoryUrn ??
      (coach.categoryInformationList &&
      coach.categoryInformationList.length === 1
        ? coach.categoryInformationList[0].category.id
        : null),
  );

  const [didGoToCategorySelection, setDidGoToCategorySelection] =
    useState<boolean>(false);
  const selectedCycle = selectedTime
    ? coach.coachingTimeSummary?.cycles?.find(
        (cycle) =>
          (cycle.start ?? 0) <= selectedTime &&
          selectedTime <= (cycle.end ?? 0),
      )
    : null;

  useEffect(() => {
    if (open) {
      mxpnl.track(SCHEDULE_MODAL_EVENTS.STEP_CHANGE, {
        coach_urn: coach.id,
        category_urn: categoryToUse,
        session_type: sessionType,
        step: step,
      });
      phog.capture('schedule_modal--step_change', {
        coach_urn: coach.id,
        category_urn: categoryToUse,
        session_type: sessionType,
        step: step,
      });
    }
  }, [step, categoryToUse, sessionType, coach.id, open]);

  const apolloClient = useApolloClient();
  const { data: userData } = useScheduleModalUserQuery();

  const { data: unassignedOrdersData } =
    useScheduleModalGetUnassignedOrdersQuery({
      variables: {
        coachUrn: coach.id,
      },
    });

  const unassignedOrders = useMemo(() => {
    return (unassignedOrdersData?.unassignedOrders ?? []).filter(
      (o) => !!categoryToUse && o.category?.id === categoryToUse,
    );
  }, [unassignedOrdersData, categoryToUse]);

  const existingBalanceFromSession = coachingSession
    ? coachingSession.durationInMinute
    : 0;

  const selectedUnassignedOrderTime = useMemo(() => {
    return (
      orderToAssign?.hourlyOrder?.timeInMinute ??
      orderToAssign?.subscriptionOrder?.timeInMinute ??
      (orderToAssign?.packageOrder?.prices?.[0]?.packageCoachingTime?.estimate
        ? sessionLength
        : (orderToAssign?.packageOrder?.prices?.[0]?.packageCoachingTime
            ?.exactTimeInMinute ?? 0))
    );
  }, [orderToAssign, sessionLength]);

  const timeRequiredForSession = Math.max(
    sessionLength - existingBalanceFromSession - selectedUnassignedOrderTime,
    0,
  );

  const onScheduleWrapper = (scheduledSession: {
    id?: Scalars['CoachingSessionUrn']['output'];
    startAt?: number;
  }) => {
    if (scheduledSession.id) {
      setScheduledSessionId(scheduledSession.id);
    }
    if (onSchedule && scheduledSession.startAt) {
      onSchedule(scheduledSession.startAt);
    }
  };

  useEffect(() => {
    const calcEnoughTimeToSchedule = async (values: {
      coachUrn: string;
      startAt: number;
      endAt: number;
    }) => {
      const resp = await enoughTimeToScheduleAction({
        variables: values,
      });
      if (resp.data) {
        setEnoughSelfServeTimeToSchedule(resp.data.enoughTimeToSchedule);
      }
    };

    if (selectedTime) {
      void calcEnoughTimeToSchedule({
        coachUrn: coach.id,
        startAt: selectedTime,
        endAt: selectedTime + timeRequiredForSession * MINUTE,
      });
    }
  }, [
    coach.id,
    enoughTimeToScheduleAction,
    selectedTime,
    sessionLength,
    timeRequiredForSession,
  ]);

  const subscriptionMemberCanBook = useMemo(() => {
    if (!coach.coachingTimeSummary?.cycles || !selectedTime) {
      return enoughSelfServeTimeToSchedule;
    }
    if (
      hasUnlimitedPackage ||
      availableCoachingTimeInMinutes >= timeRequiredForSession
    ) {
      return true;
    }
    if (!selectedTime) return false;

    if (selectedCycle && selectedCycle.timeInMinute >= sessionLength) {
      return true;
    }

    return false;
  }, [
    coach.coachingTimeSummary?.cycles,
    selectedTime,
    hasUnlimitedPackage,
    availableCoachingTimeInMinutes,
    timeRequiredForSession,
    selectedCycle,
    sessionLength,
    enoughSelfServeTimeToSchedule,
  ]);

  const shouldShowPurchaseTimeScreen = useMemo(() => {
    if (sessionType === SessionOption.INTRO_CALL) return false;

    return !subscriptionMemberCanBook;
  }, [
    timeRequiredForSession,
    availableCoachingTimeInMinutes,
    sessionType,
    subscriptionMemberCanBook,
  ]);

  const tier = coach.tier ?? userData?.user?.applicant?.lelandMembership?.tier;

  const totalAvailableTime =
    availableTime + (coachingSession?.durationInMinute ?? 0);

  const { data: singleHourData } = useScheduleModalMatchSkusByTimeQuery({
    variables: {
      tier: tier,
    },
    skip: !tier || sessionLength <= totalAvailableTime,
  });

  const basePrice = singleHourData?.matchSkusByTime?.[0]?.price ?? 0;
  const additionalTimeNeeded =
    sessionLength - (selectedCycle?.timeInMinute ?? totalAvailableTime);
  const sessionPrice =
    sessionLength <= totalAvailableTime
      ? 0
      : (basePrice / 60) * additionalTimeNeeded;

  const hasRelationship = !!coach.coachingRelationship;

  const onChangeSelectedTime = useCallback(
    (time: Possible<number>) => {
      if (!time) {
        setStep(ScheduleModalStep.CALENDAR);
        setRecurringCadence(null);
      }
      setSelectedTime(time);
      if (recurringCadence && time) {
        const dateInfo = getDateInfo(new Date(time), sessionLength);
        setRecurringCadence({
          ...recurringCadence,
          durationMinutes: sessionLength,
          startHour: dateInfo.startHour,
          startMinute: dateInfo.startMinute,
          dayOfWeek: dateInfo.dayOfWeek,
          weekOfMonth: dateInfo.weekOfMonth,
        });
      }
    },
    [setStep, setSelectedTime, recurringCadence, sessionLength],
  );

  const setSessionType = (sessionType: SessionOption) => {
    setSessionTypeDefault((oldType) => {
      if (sessionType === SessionOption.INTRO_CALL) {
        setSessionLength(15);
      } else if (
        oldType === SessionOption.INTRO_CALL &&
        sessionType === SessionOption.COACHING_SESSION
      ) {
        setSessionLength(60);
      }
      return sessionType;
    });
  };

  useEffect(() => {
    if (defaultSessionType) {
      setSessionType(defaultSessionType);
    }
  }, [defaultSessionType]);

  const setOpen = useCallback(
    async (open: boolean) => {
      setOpenDirect(open);
      if (step === ScheduleModalStep.SUCCESS) {
        if (refetchQueries) {
          await apolloClient.refetchQueries({ include: refetchQueries });
        }
        if (onSuccessModalDismiss) {
          onSuccessModalDismiss();
        }
      }
    },
    [apolloClient, onSuccessModalDismiss, refetchQueries, setOpenDirect, step],
  );

  const showInterestSelectionPage = isLexEnabled(
    LexSlug.INTRO_CALL_SHOW_INTEREST_SELECTION,
  );

  const onMoveToSubmissionStep = useCallback(
    (isComingFromCategorySelection: boolean) => {
      if (
        !coachingSession &&
        (!(categoryToUse ?? isComingFromCategorySelection) ||
          (step !== ScheduleModalStep.CATEGORY_SELECTION &&
            didGoToCategorySelection))
      ) {
        setDidGoToCategorySelection(true);
        setStep(ScheduleModalStep.CATEGORY_SELECTION);
      } else if (
        showInterestSelectionPage &&
        sessionType === SessionOption.INTRO_CALL &&
        !coachingSession
      ) {
        setStep(ScheduleModalStep.PKG_SELECTION);
      } else if (
        sessionType !== SessionOption.INTRO_CALL &&
        unassignedOrders.length > 0 &&
        additionalTimeNeeded > 0 &&
        step != ScheduleModalStep.ASSIGN_ORDER
      ) {
        setStep(ScheduleModalStep.ASSIGN_ORDER);
      } else if (shouldShowPurchaseTimeScreen) {
        setStep(ScheduleModalStep.PURCHASE);
      } else if (
        !savedCardsData?.paymentCards.length &&
        recurringCadence &&
        isLexEnabled(LexSlug.RECURRING_SESSIONS)
      ) {
        setStep(ScheduleModalStep.PAYMENT_DETAILS);
      } else if (
        coachingSession &&
        recurringCadence &&
        justThisSession === null &&
        isLexEnabled(LexSlug.RECURRING_SESSIONS)
      ) {
        setStep(ScheduleModalStep.RECURRING_SESSION_SELECTION);
        setJustThisSession(true);
      } else {
        setStep(ScheduleModalStep.MESSAGE);
      }
    },
    [
      coachingSession,
      categoryToUse,
      step,
      didGoToCategorySelection,
      showInterestSelectionPage,
      sessionType,
      unassignedOrders.length,
      additionalTimeNeeded,
      isLexEnabled,
      shouldShowPurchaseTimeScreen,
      savedCardsData?.paymentCards.length,
      recurringCadence,
      justThisSession,
    ],
  );

  const onBackFromSubmissionClick = useCallback(() => {
    setJustThisSession(null);
    setStep(
      didGoToCategorySelection
        ? ScheduleModalStep.CATEGORY_SELECTION
        : ScheduleModalStep.CALENDAR,
    );
  }, [didGoToCategorySelection]);

  const onBackToStartClick = () => {
    setStep(ScheduleModalStep.CALENDAR);
  };

  return (
    <Modal open={open} onOpenChange={setOpen}>
      {step === ScheduleModalStep.CALENDAR ? (
        <CoachCalendar
          categoryUrn={categoryToUse}
          coach={coach}
          hasUnlimitedBookingPower={hasUnlimitedPackage}
          order={null}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          setOpen={setOpen}
          setSelectedTime={onChangeSelectedTime}
          setSessionLength={setSessionLength}
          setStep={setStep}
          sessionType={sessionType}
          setSessionType={setSessionType}
          coachingSession={coachingSession}
          isReschedule={!!coachingSession}
          sidebarLocked={sidebarLocked}
          sessionPrice={sessionPrice}
          onMoveToSubmissionStep={onMoveToSubmissionStep}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
          setRecurringCadence={setRecurringCadence}
        />
      ) : null}
      {step === ScheduleModalStep.CATEGORY_SELECTION &&
      coach.categoryInformationList ? (
        <CategorySelection
          coach={coach}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          setSelectedTime={onChangeSelectedTime}
          sessionType={sessionType}
          categorySelection={categoryToUse}
          setCategorySelection={setCategoryToUse}
          onMoveToSubmissionStep={onMoveToSubmissionStep}
          onBackClick={() => setStep(ScheduleModalStep.CALENDAR)}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
        />
      ) : null}
      {step === ScheduleModalStep.PKG_SELECTION ? (
        <PackageSelection
          categoryUrn={categoryToUse}
          coach={coach}
          isPayAsYouGo={isPayAsYouGo}
          offeringPackages={offeringPackages}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          sessionType={sessionType}
          setIsPayAsYouGo={setIsPayAsYouGo}
          setOfferingPackages={setOfferingPackages}
          setSelectedTime={onChangeSelectedTime}
          onBackClick={onBackFromSubmissionClick}
          onMoveToSubmissionStep={() => setStep(ScheduleModalStep.MESSAGE)}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
        />
      ) : null}
      {step === ScheduleModalStep.ASSIGN_ORDER ? (
        <AssignExistingOrder
          coach={coach}
          unassignedOrders={unassignedOrders}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          setSelectedTime={onChangeSelectedTime}
          unassignedOrderSelection={orderToAssign}
          setUnassignedOrderSelection={setOrderToAssign}
          onBackClick={onBackFromSubmissionClick}
          onMoveToSubmissionStep={onMoveToSubmissionStep}
          categoryUrn={categoryToUse}
          coachUrn={coach.id}
          hasRelationship={hasRelationship}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
        />
      ) : null}
      {step === ScheduleModalStep.MESSAGE ? (
        <ScheduleMessageModal
          coach={coach}
          selectedTime={selectedTime}
          orderToAssign={orderToAssign?.id}
          setSelectedTime={onChangeSelectedTime}
          sessionLength={sessionLength}
          categoryUrn={categoryToUse}
          coachingSession={coachingSession}
          onSchedule={onScheduleWrapper}
          setStep={setStep}
          setOpen={setOpen}
          sessionType={sessionType}
          offeringPackageIds={Array.from(offeringPackages.keys())}
          isPayAsYouGo={isPayAsYouGo}
          onBackClick={onBackFromSubmissionClick}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
          justThisSession={justThisSession ?? true}
        />
      ) : null}
      {selectedTime && step === ScheduleModalStep.PURCHASE ? (
        <AddAdditionalTime
          categoryUrn={categoryToUse}
          coach={coach}
          coachingSession={coachingSession}
          hasUnlimitedBookingPower={hasUnlimitedPackage}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          setSelectedTime={onChangeSelectedTime}
          sessionType={sessionType}
          sessionPrice={sessionPrice}
          orderToAssignTime={selectedUnassignedOrderTime}
          orderToAssignId={orderToAssign?.id ?? undefined}
          onBackClick={onBackFromSubmissionClick}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
        />
      ) : null}
      {step === ScheduleModalStep.PAYMENT_DETAILS ? (
        <PaymentDetails
          coach={coach}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          setSelectedTime={onChangeSelectedTime}
          sessionType={sessionType}
          onMoveToSubmissionStep={() => onMoveToSubmissionStep(false)}
          onBackClick={onBackFromSubmissionClick}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
          hasSavedCards={!!savedCardsData?.paymentCards.length}
          refetchQueries={[ScheduleModalGetSavedCardsDocument]}
        />
      ) : null}
      {step === ScheduleModalStep.RECURRING_SESSION_SELECTION ? (
        <RecurringSessionDetails
          coach={coach}
          selectedTime={selectedTime}
          sessionLength={sessionLength}
          originalSessionTime={coachingSession?.startAt ?? 0}
          originalCadence={coachingSession?.recurringSession?.cadence ?? null}
          setSelectedTime={onChangeSelectedTime}
          sessionType={sessionType}
          onMoveToSubmissionStep={() => onMoveToSubmissionStep(false)}
          onBackClick={onBackFromSubmissionClick}
          onBackToStart={onBackToStartClick}
          recurringCadence={recurringCadence}
          justThisSession={justThisSession ?? true}
          setJustThisSession={setJustThisSession}
        />
      ) : null}
      {step === ScheduleModalStep.SUCCESS ? (
        <SchedulingSuccessModal
          coach={coach}
          selectedTime={selectedTime}
          setSelectedTime={onChangeSelectedTime}
          sessionType={sessionType}
          sessionLength={sessionLength}
          onBackClick={onBackFromSubmissionClick}
          onBackToStart={onBackToStartClick}
          categoryUrn={categoryToUse}
          refetchQueries={refetchQueries}
          scheduledSessionId={scheduledSessionId}
          recurringCadence={recurringCadence}
          setOpen={setOpen}
        />
      ) : null}
    </Modal>
  );
};

export const useScheduleModal: ModalHook<ScheduleModalHookProps> = () => {
  const [open, setOpen] = useControlledModalState();
  const onModalSuccess = () => {
    setOpen(false);
    mxpnl.track(SCHEDULE_MODAL_EVENTS.CLOSE);
    phog.capture('schedule_modal--close');
  };

  const ScheduleModal: FC<ScheduleModalHookProps> = (props) => {
    return open ? (
      <ScheduleModalContent
        {...props}
        open={open}
        setOpen={setOpen}
        onSuccessModalDismiss={onModalSuccess}
      />
    ) : null;
  };
  return { open, setOpen, Modal: ScheduleModal };
};
